// src/components/Pages/Subscription.js

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';

import Copyright from '../Apm/Other/Copyright';
import SubscriptionTypes from '../Apm/Faq/SubscriptionTypes';
import { maxNumberAllowedPerSubscription, tiers } from '../Data/generalData';
import { useRetrieveUserQuery } from '../../redux/features/authApi';

//----------------------------------- CONSTANTS -----------------------------------//

const SUBSCRIPTION_CONFIG = {
  free: {
    labelKey: 'subscription.free',
    priceIndex: 15,
    tierIndex: 0,
  },

  pro: {
    labelKey: 'subscription.pro',
    priceIndex: 16,
    tierIndex: 1,
  },

  premium: {
    labelKey: 'subscription.premium',
    priceIndex: 17,
    tierIndex: 2,
  },
};

//----------------------------------- UTILS -----------------------------------//

const getSubscriptionKey = (subscriptionType) => {
  const value = String(subscriptionType ?? '').toLowerCase();

  if (value.includes('premium')) {
    return 'premium';
  }

  if (value.includes('pro')) {
    return 'pro';
  }

  if (value.includes('free')) {
    return 'free';
  }

  return null;
};

const getMonthlyPrice = (priceIndex) => {
  if (typeof priceIndex !== 'number') {
    return null;
  }

  const value = Number(maxNumberAllowedPerSubscription[priceIndex]);

  return Number.isFinite(value) ? value : null;
};

//----------------------------------- COMPONENT -----------------------------------//

const Subscription = () => {
  const { t } = useTranslation();

  const {
    data: account,
    isLoading: isAccountLoading,
    isFetching: isAccountFetching,
    isError: isAccountError,
  } = useRetrieveUserQuery();

  const subscriptionKey = getSubscriptionKey(account?.subscription_type);

  const subscriptionConfig = subscriptionKey ? SUBSCRIPTION_CONFIG[subscriptionKey] : null;

  const subscriptionTiers = useMemo(() => tiers(), []);

  const subscriptionLabel = subscriptionConfig
    ? t(subscriptionConfig.labelKey)
    : t('subscription.unknown', {
        defaultValue: 'Unknown',
      });

  const monthlyPrice = getMonthlyPrice(subscriptionConfig?.priceIndex);

  const subscriptionColor = subscriptionConfig
    ? subscriptionTiers[subscriptionConfig.tierIndex]?.backgroundColor
    : undefined;

  if (isAccountLoading && !account) {
    return (
      <Container
        maxWidth="lg"
        component="main"
        sx={{
          py: {
            xs: 4,
            md: 8,
          },
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: 300,
          }}
        >
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (isAccountError && !account) {
    return (
      <Container
        maxWidth="lg"
        component="main"
        sx={{
          py: {
            xs: 4,
            md: 8,
          },
        }}
      >
        <Typography color="error" align="center">
          {t('toast.account_could_not_be_loaded', {
            defaultValue: 'Account could not be loaded.',
          })}
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      component="main"
      sx={{
        py: {
          xs: 3,
          sm: 5,
          md: 8,
        },

        px: {
          xs: 2,
          sm: 3,
        },
      }}
    >
      <Card>
        <CardHeader
          title={t('general.subscription')}
          titleTypographyProps={{
            fontWeight: 600,
          }}
          action={isAccountFetching ? <CircularProgress size={20} /> : null}
          sx={{
            backgroundColor: 'action.hover',
          }}
        />

        <Divider />

        <CardContent
          sx={{
            py: {
              xs: 3,
              md: 5,
            },
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={{
              xs: 2,
              md: 5,
            }}
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                flex: {
                  md: '0 0 33%',
                },
              }}
            >
              {t('subscription.your_subscription')}
            </Typography>

            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={{
                xs: 1,
                sm: 2,
              }}
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: subscriptionColor ?? 'text.primary',

                  fontWeight: 700,
                }}
              >
                {subscriptionLabel}
              </Typography>

              {monthlyPrice !== null && (
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <EuroSymbolIcon
                    sx={{
                      alignSelf: 'center',
                    }}
                  />

                  <Typography variant="h4" component="span">
                    {monthlyPrice}
                  </Typography>

                  <Typography variant="h5" component="span">
                    {t('subscription.slash_month')}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Box
        component="section"
        aria-labelledby="subscription-types-title"
        sx={{
          pt: {
            xs: 6,
            md: 10,
          },
        }}
      >
        <Typography
          id="subscription-types-title"
          component="h1"
          variant="h2"
          align="center"
          color="text.primary"
          sx={{
            mb: {
              xs: 4,
              md: 6,
            },

            fontSize: {
              xs: '2.25rem',
              md: '3.75rem',
            },
          }}
        >
          {t('subscription.subscriptions')}
        </Typography>

        <SubscriptionTypes />
      </Box>

      <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: 'divider',

          mt: {
            xs: 6,
            md: 8,
          },

          pt: {
            xs: 4,
            md: 6,
          },

          pb: {
            xs: 3,
            md: 6,
          },
        }}
      >
        <Copyright />
      </Box>
    </Container>
  );
};

export default Subscription;
